import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { ladeBildHoch, formatGroesse } from '../components/BildAuswahl';

function fmtDatum(d) { return d ? new Date(d).toLocaleDateString('de-DE') : '–'; }

function verwendungsLink(v) {
  return v.typ === 'kachel'
    ? { to: '/kacheln', text: `Kachel „${v.titel}"` }
    : { to: `/angebote/${v.id}/bearbeiten`, text: `Angebot „${v.titel}"` };
}

export default function Bilder() {
  const { user } = useAuth();
  const darfHochladen = user?.rolle !== 'readonly';
  const darfLoeschen = ['admin', 'schwimmadmin'].includes(user?.rolle);

  const [bilder, setBilder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState([]);
  const [hinweis, setHinweis] = useState('');
  const [fortschritt, setFortschritt] = useState(null);   // { aktuell, gesamt }
  const [loeschend, setLoeschend] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/bilder');
      setBilder(r.data);
    } catch {
      setFehler(['Die Bildbibliothek konnte nicht geladen werden.']);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mehrere Dateien nacheinander statt gleichzeitig: Der Server komprimiert
  // jedes Bild, und er teilt sich seinen Arbeitsspeicher mit SwimCoach.
  const hochladen = async (e) => {
    const dateien = Array.from(e.target.files || []);
    e.target.value = '';
    if (!dateien.length) return;

    setFehler([]);
    setHinweis('');
    const meldungen = [];
    let erfolgreich = 0;
    let eingespart = 0;

    for (let i = 0; i < dateien.length; i++) {
      setFortschritt({ aktuell: i + 1, gesamt: dateien.length });
      try {
        const bild = await ladeBildHoch(dateien[i]);
        erfolgreich++;
        eingespart += Math.max(0, (bild.original_groesse_bytes || 0) - (bild.groesse_bytes || 0));
      } catch (err) {
        meldungen.push(err.message);
      }
    }

    setFortschritt(null);
    setFehler(meldungen);
    if (erfolgreich) {
      setHinweis(`${erfolgreich} ${erfolgreich === 1 ? 'Bild' : 'Bilder'} hochgeladen – dabei ${formatGroesse(eingespart)} eingespart.`);
    }
    await load();
  };

  const loeschen = async (b) => {
    if (!window.confirm(`Bild „${b.original_name || b.pfad}" endgültig löschen?`)) return;
    setLoeschend(b.id);
    setFehler([]);
    setHinweis('');
    try {
      await api.delete(`/api/admin/bilder/${b.id}`);
      await load();
    } catch (err) {
      setFehler([err.response?.data?.error || 'Löschen fehlgeschlagen.']);
    } finally {
      setLoeschend(null);
    }
  };

  const unbenutzt = bilder.filter(b => b.verwendet_in.length === 0).length;
  const gesamtGroesse = bilder.reduce((s, b) => s + (b.groesse_bytes || 0), 0);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Bildbibliothek</h2>
        {darfHochladen && (
          <label className="btn btn-primary btn-sm" style={{ cursor: fortschritt ? 'wait' : 'pointer', opacity: fortschritt ? 0.6 : 1 }}>
            {fortschritt ? `Lade hoch… ${fortschritt.aktuell}/${fortschritt.gesamt}` : '⬆ Bilder hochladen'}
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple
              style={{ display: 'none' }} disabled={Boolean(fortschritt)} onChange={hochladen} />
          </label>
        )}
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem', maxWidth: 820 }}>
        Alle Bilder für Kacheln und Angebote an einem Ort. Jedes Bild lässt sich mehrfach verwenden –
        ausgewählt wird es direkt bei der <Link to="/kacheln">Kachel</Link> bzw. im Angebot über „🖼 Bild".
        Hochgeladen werden JPG, PNG oder WebP bis 2 MB; der Server verkleinert jedes Bild automatisch auf
        höchstens 500 KB und entfernt eingebettete Metadaten wie den Aufnahmeort.
      </p>

      {hinweis && <div className="alert alert-success">{hinweis}</div>}
      {fehler.map((f, i) => <div key={i} className="alert alert-error">{f}</div>)}

      {!loading && bilder.length > 0 && (
        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          {bilder.length} {bilder.length === 1 ? 'Bild' : 'Bilder'} · zusammen {formatGroesse(gesamtGroesse)}
          {unbenutzt > 0 && ` · ${unbenutzt} unbenutzt`}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      ) : bilder.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">🖼</div>
            <div className="empty-state-text">Noch keine Bilder in der Bibliothek</div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '1rem' }}>
          {bilder.map(b => {
            const benutzt = b.verwendet_in.length > 0;
            return (
              <div key={b.id} className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: 150, background: '#f0f4f8' }}>
                  {b.datei_fehlt ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c62828', fontSize: '0.85rem' }}>
                      ⚠ Datei fehlt
                    </div>
                  ) : (
                    <a href={`/uploads/${b.pfad}`} target="_blank" rel="noopener noreferrer" title="In voller Größe öffnen">
                      <img src={`/uploads/${b.pfad}`} alt={b.original_name || ''} loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </a>
                  )}
                </div>

                <div style={{ padding: '0.75rem 0.875rem', flex: 1, fontSize: '0.8rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={b.original_name || b.pfad}>
                    {b.original_name || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>ohne Namen (Bestand)</span>}
                  </div>
                  <div style={{ color: 'var(--text-muted)' }}>
                    {formatGroesse(b.groesse_bytes)}
                    {b.breite && b.hoehe ? ` · ${b.breite} × ${b.hoehe} px` : ''}
                  </div>
                  <div style={{ color: 'var(--text-muted)' }}>
                    {fmtDatum(b.created_at)}{b.hochgeladen_von_name ? ` · ${b.hochgeladen_von_name}` : ''}
                  </div>

                  <div style={{ marginTop: '0.5rem' }}>
                    {benutzt ? (
                      <>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Verwendet in:</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {b.verwendet_in.map(v => {
                            const l = verwendungsLink(v);
                            return (
                              <Link key={`${v.typ}-${v.id}`} to={l.to} style={{
                                padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', textDecoration: 'none',
                                background: v.typ === 'kachel' ? '#e3f2fd' : '#fff3e0',
                                color: v.typ === 'kachel' ? '#1565c0' : '#e65100',
                              }}>
                                {l.text}
                              </Link>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', background: '#f5f5f5', color: '#757575' }}>
                        unbenutzt
                      </span>
                    )}
                  </div>
                </div>

                {darfLoeschen && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '0.5rem 0.875rem', textAlign: 'right' }}>
                    <button className="btn btn-danger btn-sm" onClick={() => loeschen(b)}
                      disabled={benutzt || loeschend === b.id}
                      title={benutzt ? 'Wird noch verwendet – dort zuerst ein anderes Bild wählen' : 'Bild endgültig löschen'}>
                      {loeschend === b.id ? '…' : 'Löschen'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
